import {
  APICallState,
  MFAChannel,
  MFAPhone,
  PanelContainer,
  Switch,
  Toast,
  ToastMessageContentItemType,
  ToastType,
  useIsDesktop,
} from '@eCommerce/frontend-legacy-shared-library-native';
import {Flex, Text, View} from 'native-base';
import React, {useCallback, useEffect, useState} from 'react';

import {maskEmail, maskPhoneNumber} from 'utils/phoneNumbers';

import VerificationMethodDisplay from './VerificationMethodDisplay';
import {useTheme} from 'styled-components/native';
import {AdobeEvent, useTrackEvent} from 'hooks/useTrackEvent';
import OptOutModule from './OptOutModule';

export interface SecurityPanelProps {
  email: string | undefined;
  phoneNumber: MFAPhone | undefined;
  channelPreference: MFAChannel | undefined;
  isVIP: boolean;
  isUnlocked: boolean;
  isMFATokenValidationSuccess: boolean;
  savingState: APICallState;
  editOptinState: APICallState;
  onEdit: () => void;
  onSave: (channel: MFAChannel) => void;
  onCancel: () => void;
  onOptout: () => void;
  onOverlayAnimationFinish: () => void;
  isEditLoading: boolean;
  setMFAWebViewUrl: (url: string) => void;
  isLoading: boolean;
}

const SecurityPanel: React.FC<SecurityPanelProps> = props => {
  const {
    email,
    phoneNumber,
    channelPreference,
    isVIP,
    isMFATokenValidationSuccess,
    isUnlocked,
    onEdit,
    onSave,
    onCancel,
    savingState,
    editOptinState,
    onOptout,
    onOverlayAnimationFinish,
    isEditLoading,
    setMFAWebViewUrl,
    isLoading,
  } = props;
  const isDesktop = useIsDesktop();
  const {trackAdobeEvent} = useTrackEvent();
  const theme = useTheme() as any;
  const {tuna} = theme.colors;

  const [selectedChannel, setSelectedChannel] = useState(channelPreference);
  const [isOptoutModalOpen, setIsOptoutModalOpen] = useState(false);

  const onSwitchToggle = () => {
    trackAdobeEvent({
      event: AdobeEvent.click,
      click: {name: 'mfa enrolled opt-out'},
    });
    setIsOptoutModalOpen(true);
  };

  const onPrimaryPress = () => {
    trackAdobeEvent({
      event: AdobeEvent.click,
      click: {
        name: 'mfa enrolled opt-out confirm',
      },
    });

    onOptout();
  };

  const onSecondaryPress = () => {
    trackAdobeEvent({
      event: AdobeEvent.click,
      click: {
        name: 'mfa enrolled opt-out cancel',
      },
    });

    setIsOptoutModalOpen(false);
  };

  const renderToggle = () => {
    return (
      <Flex flexDirection="row" alignItems="center" justifyContent="flex-end">
        <Text
          accessibilityLabel="Multi-factor authentication status: opted in"
          mr={2}>
          Opted-in
        </Text>

        <Switch
          accessibilityLabel="Opt out of multi-factor authentication"
          isChecked
          onToggle={onSwitchToggle}
        />
      </Flex>
    );
  };
  const isFullyUnlocked = isUnlocked && isMFATokenValidationSuccess;

  const onVerificationMethodPressed = useCallback(
    (verificationMethod: MFAChannel | undefined) => {
      if (isFullyUnlocked && verificationMethod) {
        setSelectedChannel(verificationMethod);
      }
    },
    [isFullyUnlocked]
  );

  const onSavePress = () => {
    if (selectedChannel && selectedChannel !== channelPreference) {
      onSave(selectedChannel);
    } else {
      onCancel();
    }
  };
  useEffect(() => {
    setSelectedChannel(prevState => {
      if (prevState !== channelPreference) {
        return channelPreference;
      }
      return prevState;
    });
  }, [channelPreference]);

  return (
    <PanelContainer
      isLoading={isEditLoading || isLoading}
      testID="security-panel"
      panelTitle="Two-Step Verification"
      icon="shield-alt"
      showEdit={!isFullyUnlocked}
      customSideButton={isFullyUnlocked && renderToggle()}
      isSaving={savingState === APICallState.LOADING}
      onEdit={onEdit}
      onCancel={onCancel}
      onSave={onSavePress}
      hasFooter={isFullyUnlocked}
      contentMaxWidth={isDesktop ? undefined : '100%'}
      apiCallState={savingState}
      onOverlayAnimationFinish={onOverlayAnimationFinish}>
      {isUnlocked && (
        <Text color={tuna} fontFamily="body" fontSize={isDesktop ? 4 : 3}>
          Choose your preference:
        </Text>
      )}
      <View mb={isDesktop ? 5 : 4} />
      {isUnlocked || channelPreference === MFAChannel.EMAIL ? (
        <VerificationMethodDisplay
          setMFAWebViewUrl={setMFAWebViewUrl}
          contactInfo={email && maskEmail(email)}
          isSelected={isFullyUnlocked && selectedChannel === MFAChannel.EMAIL} //only for read only, else we will want it toggle-able
          isPreferredChannel={channelPreference === MFAChannel.EMAIL}
          onPressed={() =>
            onVerificationMethodPressed(email ? MFAChannel.EMAIL : undefined)
          }
          verificationMethod={MFAChannel.EMAIL}
          /**
           * We can assume whatever info user has on file is already verified
           * But we still want to display the verified flag.
           */
          isVerified={true}
          editing={isUnlocked}
        />
      ) : undefined}

      {isUnlocked && <View h={isDesktop ? '38px' : '16px'} />}
      {isUnlocked || channelPreference === MFAChannel.SMS ? (
        <VerificationMethodDisplay
          setMFAWebViewUrl={setMFAWebViewUrl}
          contactInfo={
            phoneNumber ? maskPhoneNumber(phoneNumber.value) : undefined
          }
          isSelected={isFullyUnlocked && selectedChannel === MFAChannel.SMS}
          isPreferredChannel={channelPreference === MFAChannel.SMS}
          onPressed={() =>
            onVerificationMethodPressed(
              phoneNumber ? MFAChannel.SMS : undefined
            )
          }
          verificationMethod={MFAChannel.SMS}
          /**
           * We can assume whatever info user has on file is already verified
           * But we still want to display the verified flag.
           */
          isVerified={true}
          editing={isUnlocked}
        />
      ) : undefined}
      {phoneNumber && phoneNumber?.countryCode !== '1' && (
        <View mt={4} width={isDesktop ? '874px' : '100%'}>
          <Toast
            toast={{
              type: ToastType.DANGER,
              messageContent: [
                {
                  elementType: ToastMessageContentItemType.TEXT,
                  text: 'SMS Verification is only available in the United States and Canada.  Please choose email as your verification method',
                },
              ],
            }}
          />
        </View>
      )}
      {(savingState === APICallState.ERROR ||
        editOptinState === APICallState.ERROR) && (
        <View mt={4} width={isDesktop ? '874px' : '100%'}>
          <Toast
            toast={{
              type: ToastType.DANGER,
              messageContent: [
                {
                  elementType: ToastMessageContentItemType.TEXT,
                  text: 'Sorry, we were unable to update your security preferences. If you continue to experience issues please contact support.',
                },
              ],
            }}
          />
        </View>
      )}
      <OptOutModule
        isOpen={isOptoutModalOpen}
        onClose={() => setIsOptoutModalOpen(false)}
        onPrimaryPress={onPrimaryPress}
        onSecondaryPress={onSecondaryPress}
      />
    </PanelContainer>
  );
};

export default SecurityPanel;
